import React, { useEffect, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { MDialog } from 'common';
import { formatNumberWithCommas } from 'utils/helpers/format';

import { useUserRolePermissions } from 'hooks';
import { func, number, shape, string } from 'prop-types';
import * as R from 'ramda';
import { REQUIRED } from 'utils/helpers/validationMessages';
import { useDispatch } from 'react-redux';
import { createSnack } from 'snackbar/snackbarSlice';

import renderInput from './renderUtils';

import {
  useCreateAutoReconsignmentMutation,
  useEditAutoReconsignmentMutation,
  useGetAutoReconsignmentfromLocationsQuery,
  useGetAutoReconsignmentQuery,
  useViewAutoReconsignmentQuery,
} from '../autoReconsignmentsEndpoints';
import { useGetAffiliateProductsQuery, useGetDestinationsQuery } from '../reconsignmentsEndpoints';

const AutoDrawer = ({
  autoReconsignmentsData,
  selectedReconsignment,
  setSelectedReconsignment,
  modalMode,
  setCopiedReconsignment,
}) => {
  const { currentAffiliate, isProductOwnerManage, isProductOwnerView } = useUserRolePermissions();
  const canManage = Boolean(isProductOwnerManage && isProductOwnerView);
  const viewOnly = !canManage || modalMode === 'view';

  const [reconsignment, setReconsignment] = useState({ isActive: true });
  const [validation, setValidation] = useState({
    thresholdVolume: '',
    toLocation: false,
    volume: '',
  });
  const [disableSave, setDisableSave] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [disableSubmit, setDisalbeSubmit] = useState(false);
  // Set up save
  // body set
  // hit edit endpoint
  // on success close drawer
  // show snackbar
  const body = R.not(R.isEmpty(reconsignment)) && {
    fromLocationId: R.path(['fromLocation', 'locationId'], reconsignment),
    inventoryOwner: {
      companyCustomerIdName: R.prop('customerIdName', currentAffiliate),
      dataCompanyId: R.prop('dataCompanyId', currentAffiliate),
    },
    isActive: R.prop('isActive', reconsignment),
    productId: R.path(['product', 'productId'], reconsignment),
    reconsignmentId: R.prop('reconsignmentId', reconsignment),
    thresholdVolume: R.prop('thresholdVolume', reconsignment),
    toLocationId: R.path(['toLocation', 'locationId'], reconsignment),
    transferDate: R.prop('transferDate', reconsignment),
    volume: R.prop('volume', reconsignment),
  };
  const [submit, { isLoading: isUpdating }] = useEditAutoReconsignmentMutation();
  const [submitCopy] = useCreateAutoReconsignmentMutation();

  const dispatch = useDispatch();

  const handleSubmit = () => {
    const bodyForDuplication = {
      ...body,
      consignmentStatus: R.prop('consignmentStatus', reconsignment),
      fromLocationIds: [R.path(['fromLocation', 'locationId'], reconsignment)],
      inventoryOwner: {
        companyCustomerIdName: R.prop('customerIdName', currentAffiliate),
        dataCompanyId: R.prop('dataCompanyId', currentAffiliate),
      },
      inventoryStatus: R.prop('inventoryStatus', reconsignment),
      isActive: R.prop('isActive', reconsignment),
      originIds: R.prop('originIds', reconsignment),
      productId: R.path(['product', 'productId'], reconsignment),
      productStatus: R.path(['product', 'productStatus'], reconsignment),
      thresholdVolume: R.prop('thresholdVolume', reconsignment),
      toLocationIds: [R.path(['toLocation', 'locationId'], reconsignment)],
      volume: R.prop('volume', reconsignment),
    };

    submitCopy({ body: bodyForDuplication })
      .then(response => {
        if (response.error) {
          dispatch(
            createSnack({
              message: response.error.data?.title || 'Failed to duplicate the auto reconsignment',
              options: { variant: 'error' },
            })
          );
        } else {
          dispatch(
            createSnack({
              message: 'Auto reconsignment duplicated successfully',
            })
          );

          setSelectedReconsignment(null);
          setCopiedReconsignment(null);
        }
      })
      .finally(() => setTimeout(() => setSelectedReconsignment(null), 500));
  };

  const handleSave = () => {
    submit({ body })
      .then(res => {
        if (res.error) {
          dispatch(
            createSnack({
              message: res.error.data.title,
              options: { variant: 'error' },
            })
          );
        } else {
          dispatch(
            createSnack({
              message: res.data.message,
            })
          );
        }
      })
      .finally(() => setTimeout(() => setSelectedReconsignment(null), 500));
  };

  const params = {
    inventoryOwner: {
      companyCustomerIdName: currentAffiliate?.customerIdName,
      dataCompanyId: currentAffiliate?.dataCompanyId,
    },
    reconsignmentId: selectedReconsignment,
  };

  // Query for manage users
  const { data: manageData, isFetching: isFetchingManage } = useGetAutoReconsignmentQuery(
    { params },
    { skip: R.isNil(currentAffiliate) || viewOnly }
  );

  // Query for view-only users
  const { data: viewData, isFetching: isFetchingView } = useViewAutoReconsignmentQuery(
    {
      params,
    },
    { skip: R.isNil(currentAffiliate) || canManage }
  );

  // Use correct data based on permission
  const data = canManage ? manageData : viewData;
  const isFetchingDetail = canManage ? isFetchingManage : isFetchingView;
  const controlsDisabled = isFetchingDetail || viewOnly;

  useEffect(() => {
    if (R.not(R.isEmpty(data))) {
      setReconsignment(
        R.evolve(
          {
            rvpValue: R.equals(R.T(), R.path(['product', 'isGasoline'], data))
              ? R.always(R.prop('rvpValue', data))
              : R.always('Any'),
          },
          data
        )
      );
    }
  }, [data]);

  const { data: products = [], isFetching: isLoadingProducts } = useGetAffiliateProductsQuery(
    { params },
    { skip: R.isNil(currentAffiliate) || viewOnly }
  );

  const destinationParams = {
    ...params,
    productId: R.path(['product', 'productId'], reconsignment),
  };
  const destinationBaseParams = R.assoc(
    'locationId',
    R.path(['fromLocation', 'locationId'], reconsignment),
    destinationParams
  );

  const destinationSkip = [
    R.isNil(currentAffiliate),
    R.isEmpty(reconsignment),
    R.isNil(R.path(['fromLocation', 'locationId'], reconsignment)),
  ];

  const { data: destinations = [], isFetching: isLoadingDestinations } = useGetDestinationsQuery(
    { params: destinationBaseParams },
    { skip: R.any(R.equals(true))(destinationSkip) || viewOnly }
  );

  const locationParams = {
    ...params,
    productId: R.path(['product', 'productId'], reconsignment),
  };

  const locationSkip = [R.isNil(currentAffiliate), R.isEmpty(reconsignment)];
  const hasFromLocation = R.not(R.isNil(R.path(['fromLocation', 'locationId'], reconsignment)));
  const selectedToLocationId = R.path(['toLocation', 'locationId'], reconsignment);
  const {
    data: locations = [],
    isFetching: isLoadingLocations,
    isSuccess: isLocationsSuccess,
  } = useGetAutoReconsignmentfromLocationsQuery(
    { params: locationParams },
    { skip: R.any(R.equals(true))(locationSkip) || viewOnly }
  );
  const sanitizeVolume = R.compose(R.replace(/[^0-9]/g, ''), R.path(['target', 'value']));

  // validateVolume :: Number -> String
  const validateVolume = R.cond([
    [R.equals(NaN), R.always(REQUIRED)],
    [R.gt(1000), R.always('Volume must be a whole number greater than or equal to 1,000')],
    [R.T, R.always('')],
  ]);

  const handleVolumeChange = R.compose(
    setReconsignment,
    R.assoc('volume'),
    R.tap(R.compose(setValidation, R.assoc('volume'), validateVolume)),
    parseInt,
    sanitizeVolume
  );

  const validateThresholdVolume = R.cond([
    [R.equals(NaN), R.always(REQUIRED)],
    [R.gt(200), R.always('Volume must be a whole number greater than or equal to 200')],
    [R.T, R.always('')],
  ]);

  const handleThresholdVolumeChange = R.compose(
    setReconsignment,
    R.assoc('thresholdVolume'),
    R.tap(R.compose(setValidation, R.assoc('thresholdVolume'), validateThresholdVolume)),
    parseInt,
    sanitizeVolume
  );

  // Validations
  const isToLocationValid = R.not(R.isNil(R.prop('toLocation', reconsignment)));
  const isFromLocationValid = R.not(R.isNil(R.prop('fromLocation', reconsignment)));
  // threshold volume must be greater than 0 & whole int
  const isThresholdVolumeValid = R.and(
    R.gte(R.prop('thresholdVolume', reconsignment), 200),
    R.not(R.isEmpty(R.prop('thresholdVolume', reconsignment)))
  );
  // volume must be greater than 0 & whole int
  const isVolumeValid = R.and(
    R.gte(R.prop('volume', reconsignment), 1000),
    R.not(R.isEmpty(R.prop('volume', reconsignment)))
  );

  // Updates
  const hasActiveUpdate = R.not(
    R.equals(R.prop('isActive', reconsignment), R.prop('isActive', data))
  );

  const hasProductUpdate = R.not(
    R.equals(
      R.path(['product', 'productId'], reconsignment),
      R.path(['product', 'productId'], data)
    )
  );

  const locationPair = {
    fromLocation: R.path(['fromLocation', 'locationName'], reconsignment),
    product: R.path(['product', 'productName'], reconsignment),
    reconsignmentId: R.prop('reconsignmentId', reconsignment),
    toLocation: R.path(['toLocation', 'locationName'], reconsignment),
  };

  const hasVolumeUpdate = R.not(R.equals(R.prop('volume', reconsignment), R.prop('volume', data)));

  const existingLocationPairs = autoReconsignmentsData
    ? autoReconsignmentsData.autoReconsignments.map(autoRecon => ({
        autoReconsignmentId: autoRecon.autoReconsignmentId,
        fromLocation: autoRecon.fromLocation,
        product: autoRecon.product,
        toLocation: autoRecon.toLocation,
      }))
    : null;

  const hasThresholdVolumeUpdate = R.not(
    R.equals(R.prop('thresholdVolume', reconsignment), R.prop('thresholdVolume', data))
  );

  const hasDestinationUpdate = R.not(
    R.equals(
      R.path(['toLocation', 'locationId'], reconsignment),
      R.path(['toLocation', 'locationId'], data)
    )
  );

  const hasLocationUpdate = R.not(
    R.equals(
      R.path(['fromLocation', 'locationId'], reconsignment),
      R.path(['fromLocation', 'locationId'], data)
    )
  );

  const isExistingRecon = (current, allExisting) => {
    const matchingPair =
      allExisting &&
      allExisting.find(
        existingPair =>
          R.equals(R.prop('fromLocation', existingPair), R.prop('fromLocation', current)) &&
          R.equals(R.prop('toLocation', existingPair), R.prop('toLocation', current)) &&
          R.equals(R.prop('product', existingPair), R.prop('product', current))
      );

    if (matchingPair) {
      return R.equals(
        R.prop('autoReconsignmentId', matchingPair),
        R.prop('reconsignmentId', current)
      );
    }

    return true;
  };

  const isValidRecon = isExistingRecon(locationPair, existingLocationPairs);

  const updates = [
    hasActiveUpdate,
    hasProductUpdate,
    hasVolumeUpdate,
    hasThresholdVolumeUpdate,
    hasDestinationUpdate,
    hasLocationUpdate,
  ];

  const hasUpdates = updates.some(u => u);

  const validations = [
    isToLocationValid,
    isFromLocationValid,
    isThresholdVolumeValid,
    isVolumeValid,
  ];
  const hasValidInputs = R.all(R.equals(R.T()))(validations);

  const isValid = R.and(hasValidInputs, isValidRecon);

  useEffect(() => {
    if (!hasUpdates && !isValid) {
      setDisableSave(true);
    } else if (hasUpdates && !isValid) {
      setDisableSave(true);
    } else if (hasUpdates && isValid) {
      setDisableSave(false);
    }
    setDisalbeSubmit(!hasUpdates || !hasValidInputs);
    setDisalbeSubmit(R.not(R.and(hasUpdates, hasValidInputs)));
  }, [hasUpdates, hasValidInputs, isValid]);

  const handleClose = () => {
    if (hasUpdates && !viewOnly) {
      setOpenDialog(true);
    } else {
      setSelectedReconsignment(null);
    }
  };

  useEffect(() => {
    if (modalMode === 'view' || isLoadingDestinations || R.isNil(selectedToLocationId)) {
      return;
    }

    const hasMatchingDestination = R.any(
      R.propEq('locationId', selectedToLocationId),
      destinations
    );

    if (!hasMatchingDestination) {
      setReconsignment(prev => R.assoc('toLocation', null, prev));
      setValidation(prev => R.assoc('toLocation', true, prev));
    }
  }, [destinations, isLoadingDestinations, modalMode, selectedToLocationId]);

  return (
    <>
      <Drawer
        anchor="right"
        onClose={() => handleClose()}
        open
        slotProps={{
          paper: {
            sx: {
              overflowY: 'hidden',
              // eslint-disable-next-line sort-keys-fix/sort-keys-fix
              width: { xs: 1, lg: 390 },
            },
          },
        }}
      >
        <Box
          sx={{
            alignItems: 'center',
            boxShadow: 1,
            display: 'flex',
            justifyContent: 'space-between',
            px: 3,
            py: 1.5,
            zIndex: 1,
          }}
        >
          <Typography sx={{ color: 'text.primary', pt: '2px' }} variant="h6">
            {modalMode === 'copy'
              ? 'Copy Auto-Reconsignment'
              : !canManage || modalMode === 'view'
              ? 'View Auto-Reconsignment'
              : 'Edit Auto-Reconsignment'}
          </Typography>

          <IconButton
            aria-label="closeIcon"
            onClick={() => handleClose()}
            sx={{
              alignSelf: 'flex-end',
              padding: 0,
              pb: '2px',
            }}
          >
            <CloseRoundedIcon sx={{ color: 'primary.light' }} />
          </IconButton>
        </Box>
        <Box
          sx={{
            '&::-webkit-scrollbar': {
              display: 'none',
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
            },
            height: 'calc(100% - 129px)',
            overflowY: 'auto',
          }}
        >
          <Stack direction="column" spacing={2} sx={{ p: 3 }}>
            <Stack className="css-heap-redact" direction="column" spacing={0.5}>
              <Typography variant="caption">Reconsignment ID</Typography>
              <Typography sx={{ fontWeight: 700 }} variant="body1">
                #{R.prop('reconsignmentId', reconsignment)}
              </Typography>
            </Stack>
            <Stack className="css-heap-redact" direction="column" spacing={0.5}>
              <Typography variant="caption">Affiliate</Typography>
              <Typography sx={{ fontWeight: 700 }} variant="body1">
                {R.prop('customerNameShort', currentAffiliate)}
              </Typography>
            </Stack>
            {R.not(R.isEmpty(reconsignment)) && (
              <Stack spacing={0.5}>
                <Typography variant="caption">Origin(s)</Typography>
                {R.gte(R.length(R.prop('origins', reconsignment)), 1) ? (
                  R.prop('origins', reconsignment).map(({ originId, originName }) => (
                    <Typography key={originId} variant="body1">
                      {originName}
                    </Typography>
                  ))
                ) : (
                  <Typography variant="body1">Any</Typography>
                )}
              </Stack>
            )}
          </Stack>
          <Box sx={{ pb: 2, px: 3 }}>
            <Typography variant="caption">
              Note: source locations are constrained by the origin(s) configured above.
            </Typography>
          </Box>
          <Divider sx={{ margin: '0 auto', width: 'calc(100% - 48px)' }} />
          {R.prop('product', reconsignment) && (
            <Stack direction="column" spacing={2} sx={{ p: 3 }}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={R.prop('isActive', reconsignment)}
                      color="secondary"
                      disabled={controlsDisabled}
                      onChange={e =>
                        setReconsignment(
                          R.assoc('isActive', R.path(['target', 'checked'], e), reconsignment)
                        )
                      }
                    />
                  }
                  label="Active"
                />
              </FormGroup>
              <Autocomplete
                className="css-heap-redact"
                disableClearable
                disabled={controlsDisabled || modalMode === 'copy'}
                getOptionLabel={o => R.propOr('', 'productName', o)}
                isOptionEqualToValue={R.eqProps('productId')}
                loading={isLoadingProducts}
                onChange={(_, value) => {
                  if (R.not(R.equals(R.prop('product', reconsignment), value))) {
                    setValidation(prev => R.assoc('toLocation', false, prev));
                    setReconsignment(
                      R.evolve(
                        {
                          fromLocation: R.always(null),
                          product: R.always(value),
                          thresholdVolume: R.always(''),
                          toLocation: R.always(null),
                          volume: R.always(''),
                        },
                        reconsignment
                      )
                    );
                  }
                }}
                options={R.defaultTo([], products)}
                renderInput={renderInput({
                  label: 'Product',
                  length: modalMode === 'view' ? 1 : products?.length ?? 0,
                  loading: isLoadingProducts,
                })}
                value={R.defaultTo({}, R.prop('product', reconsignment))}
              />
              <Autocomplete
                disableClearable
                disabled={controlsDisabled}
                getOptionLabel={o => R.propOr('', 'locationName', o)}
                isOptionEqualToValue={R.eqProps('locationId')}
                loading={isLoadingLocations}
                onChange={(_, value) => {
                  setValidation(prev => R.assoc('toLocation', false, prev));
                  setReconsignment(
                    R.evolve(
                      {
                        fromLocation: R.always(value),
                        toLocation: R.always(null),
                      },
                      reconsignment
                    )
                  );
                }}
                options={isLocationsSuccess ? locations : []}
                renderInput={renderInput({
                  label: 'From Location',
                  length: modalMode === 'view' ? 1 : R.length(isLocationsSuccess ? locations : []),
                  loading: isLoadingLocations,
                })}
                value={R.prop('fromLocation', reconsignment)}
              />
              <Autocomplete
                disableClearable
                disabled={controlsDisabled || R.isNil(R.prop('fromLocation', reconsignment))}
                getOptionLabel={option => R.defaultTo('', R.prop('locationName', option))}
                isOptionEqualToValue={R.eqProps('locationId')}
                loading={isLoadingDestinations}
                onChange={(_, value) => {
                  setValidation(prev => R.assoc('toLocation', false, prev));
                  setReconsignment(
                    R.evolve({
                      toLocation: R.always(value),
                    })
                  );
                }}
                options={R.defaultTo([], destinations)}
                renderInput={renderInput({
                  error: hasFromLocation && R.prop('toLocation', validation),
                  label: 'To Location',
                  length: modalMode === 'view' ? 1 : R.length(destinations ?? []),
                  loading: isLoadingDestinations,
                })}
                value={R.prop('toLocation', reconsignment) || null}
              />
              <TextField
                className="css-heap-redact"
                disabled={controlsDisabled}
                error={R.not(R.isEmpty(validation.thresholdVolume))}
                helperText={R.prop('thresholdVolume', validation)}
                id="thresholdVolume"
                label="Threshold Volume"
                onChange={handleThresholdVolumeChange}
                slotProps={{
                  input: {
                    endAdornment: <InputAdornment position="end">bbl</InputAdornment>,
                  },
                }}
                value={formatNumberWithCommas(
                  R.defaultTo('', R.prop('thresholdVolume', reconsignment))
                )}
              />
              <TextField
                className="css-heap-redact"
                disabled={controlsDisabled}
                error={R.not(R.isEmpty(validation.volume))}
                helperText={R.prop('volume', validation)}
                id="volume"
                label="Transfer Volume"
                onChange={handleVolumeChange}
                slotProps={{
                  input: {
                    endAdornment: <InputAdornment position="end">bbl</InputAdornment>,
                  },
                }}
                value={formatNumberWithCommas(R.defaultTo('', R.prop('volume', reconsignment)))}
              />
            </Stack>
          )}
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'center',
              backgroundColor: 'surface.white',
              bottom: 0,
              boxShadow: 0,
              justifyContent: viewOnly ? 'center' : 'space-between',
              position: 'fixed',
              px: 3,
              py: 2,
              // eslint-disable-next-line sort-keys-fix/sort-keys-fix
              width: { xs: 1, lg: 390 },
              zIndex: 1,
            }}
          >
            <Stack direction="row" spacing={1} sx={viewOnly ? { mx: 'auto', width: 1 } : undefined}>
              <Button
                fullWidth={viewOnly}
                onClick={() => handleClose()}
                sx={{
                  mr: viewOnly ? 0 : 2,
                  // eslint-disable-next-line sort-keys-fix/sort-keys-fix
                  width: viewOnly ? 1 : { xs: 1, lg: 159 },
                }}
                variant="outlined"
              >
                Close
              </Button>
              {!canManage || modalMode === 'view' ? null : modalMode === 'copy' ? (
                <Button
                  disabled={disableSubmit || isFetchingDetail}
                  onClick={handleSubmit}
                  sx={{ width: { lg: 159, xs: 1 } }}
                >
                  {isUpdating ? (
                    <CircularProgress size={20} sx={{ color: 'surface.white' }} />
                  ) : (
                    'Submit'
                  )}
                </Button>
              ) : (
                <Button
                  disabled={disableSave || isFetchingDetail}
                  onClick={handleSave}
                  sx={{ width: { lg: 159, xs: 1 } }}
                >
                  {isUpdating ? (
                    <CircularProgress size={20} sx={{ color: 'surface.white' }} />
                  ) : (
                    'Save'
                  )}
                </Button>
              )}
            </Stack>
          </Stack>
        </Box>
      </Drawer>
      {!viewOnly && (
        <MDialog
          header="Unsaved Changes"
          leftButtonAction={() => {
            setOpenDialog(false);
            setSelectedReconsignment(null);
          }}
          leftButtonText="Continue"
          onClose={() => setOpenDialog(false)}
          open={openDialog}
          rightButtonAction={() => setOpenDialog(false)}
          rightButtonText="Keep Editing"
        >
          <Typography color="textPrimary" component="span" variant="subtitle1">
            Are you sure you want to leave this page? You have unsaved reconsignment changes.
          </Typography>
        </MDialog>
      )}
    </>
  );
};

AutoDrawer.propTypes = {
  autoReconsignmentsData: shape({}),
  modalMode: string.isRequired,
  selectedReconsignment: number,
  setCopiedReconsignment: func.isRequired,
  setSelectedReconsignment: func.isRequired,
};

AutoDrawer.defaultProps = {
  autoReconsignmentsData: null,
  selectedReconsignment: null,
};

export default AutoDrawer;
